import * as React from "react";
import { MesoCollapsibleGroupRegionProps } from "./typings";
import {
  BlossomComponentFactory,
  BlossomComponentFactoryProps,
} from "components/flexFramework/domain/BlossomComponentFactory";
import { logger } from "bernie-core";
import { inject, observer } from "mobx-react";
import { MESO_AD_COLLAPSE } from "config/systemEvents";
import { MarqueeGridView } from "./MarqueeGridView/MarqueeGridView";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkLayoutPosition } from "uitk-react-layout-position";

/**
 * Meso Ad Group Region wraps its ad products and collapses the region if the 0 paid ads are return from Google Ad Manager
 * @see https://pages.github.expedia.biz/Flex/blossom-flex-ui-documentation/docs/Domains_within_Blossom/Storefront_BEX_+_COMET/Native_Ad_Container/
 * @see https://pages.github.expedia.biz/Flex/blossom-flex-ui-documentation/docs/Domains_within_Blossom/Storefront_BEX_+_COMET/Meso_Ad_Group/
 * @see https://github.expedia.biz/MediaSolutions/ads-delivery-service/wiki
 */
const mesoAdGroupEventName = "collapseAdGroup";

export const MesoCollapsibleGroupRegion: React.FC<MesoCollapsibleGroupRegionProps> = logger(
  "MesoCollapsibleGroupRegion"
)(
  inject("advertising")(
    observer((props: MesoCollapsibleGroupRegionProps) => {
      const blossomComponent = BlossomComponentFactory.get(props as BlossomComponentFactoryProps);
      const mesoAdGroup: React.RefObject<JSX.IntrinsicElements["meso-collapsible-group"]> = React.createRef();
      const [collapsed, setCollapsed] = React.useState(false);
      const { context, flexComponent, advertising, l10n } = props;
      const { title, innerTitle } = flexComponent.model;

      const removeAdLoadEventHandler = () => {
        if (mesoAdGroup && mesoAdGroup.current) {
          mesoAdGroup.current.removeEventListener(mesoAdGroupEventName, handleAdGroupEvent);
        }
      };

      const handleAdGroupEvent = (event: CustomEvent) => {
        const details = (event && event.detail) || {};
        if (details.type === "meso-collapsible-group-event") {
          if (details.collapse) collapseAdGroup();
        }
      };

      const collapseAdGroup = (message?: string) => {
        setCollapsed(true);
        props.logger.logEvent(MESO_AD_COLLAPSE, "Meso Ad Group Collapsed" + message);
        removeAdLoadEventHandler();
      };

      React.useEffect(() => {
        if (mesoAdGroup && mesoAdGroup.current) {
          window.addEventListener(mesoAdGroupEventName, handleAdGroupEvent);
          mesoAdGroup.current.addEventListener(mesoAdGroupEventName, handleAdGroupEvent);
        }
        return removeAdLoadEventHandler;
      }, [mesoAdGroup.current]);

      /* istanbul ignore next */
      advertising?.includeAd?.();

      if (
        !flexComponent ||
        !flexComponent.model ||
        !blossomComponent ||
        (advertising && advertising.adsSuppressed) ||
        (advertising && advertising.adBlockerEnabled === true) ||
        collapsed
      ) {
        return null;
      }

      return (
        <UitkSpacing padding={{ blockstart: "six" }}>
          <UitkLayoutPosition>
            <meso-collapsible-group data-testid="meso-collapsible-group" ref={mesoAdGroup}>
              <MarqueeGridView
                title={title}
                innerTitle={innerTitle}
                context={context}
                l10n={l10n}
                flexComponent={flexComponent}
              />
            </meso-collapsible-group>
          </UitkLayoutPosition>
        </UitkSpacing>
      );
    })
  )
);
export default MesoCollapsibleGroupRegion;
